<template>
	<section class="m-energy" :class="{ 'm-energy--en': locale === 'en' }">
		<div v-if="dataLoading" class="energy-skeleton" aria-hidden="true">
			<section v-for="card in 2" :key="card" class="energy-skeleton__card">
				<span class="energy-skeleton__title"></span>
				<div class="energy-skeleton__body"><span></span><i></i><i></i><i></i></div>
			</section>
		</div>
		<!-- 通勝日期卡片 -->
		<div v-show="!dataLoading" ref="almanacRef" class="ts-card">
			<div class="ts-date" :class="{ animate: animateReady }">
				<p class="solar">
					{{ tsData.date }}<template v-if="tsData.week"> · {{ tsData.week }}</template>
				</p>
				<p class="lunar" :class="{ 'lunar--en': locale === 'en' }">{{ tsData.lunar }}</p>
				<div
					v-if="tsData.ganzhiItems.length"
					class="ganzhi"
					:class="{ 'ganzhi--en': locale === 'en' }"
				>
					<template v-if="locale === 'en'">
						<div
							v-for="(item, index) in tsData.ganzhiItems"
							:key="item.label || index"
							class="ganzhi-item"
						>
							<span class="ganzhi-value">{{ item.value }}</span>
							<span v-if="item.label" class="ganzhi-label">{{ item.label }}</span>
						</div>
					</template>
					<p v-else class="ganzhi-text">
						<span v-for="(item, index) in tsData.ganzhiItems" :key="index">{{ item.value }}</span>
					</p>
				</div>
			</div>
			<div class="ts-link" @click="handleAlmanacDetailClick">
				<span>{{ t("index.energy.ts.viewAll") }}</span>
				<img src="../../../images/pc/guide.png" class="arrow" alt="" />
			</div>
		</div>

		<!-- 今日能量卡片 -->
		<div v-show="!dataLoading" ref="dailyEnergyRef" class="energy-card">
			<div class="energy-main">
				<div class="gauge-section">
					<div class="gauge-wrap">
						<svg class="gauge" :key="gaugeRenderKey" viewBox="0 0 220 130" aria-hidden="true">
							<g
								v-for="(seg, index) in gaugeSegments"
								:key="index"
								:transform="`rotate(${seg.rotate}, ${seg.cx}, ${seg.cy})`"
							>
								<rect
									:x="seg.x"
									:y="seg.y"
									width="9"
									height="22"
									rx="2"
									fill="#F5EBD4"
									:class="{ animate: animateReady && seg.active }"
									:style="{ animationDelay: `${index * 0.06}s` }"
								/>
							</g>
						</svg>
						<div class="gauge-value" :class="{ animate: animateReady }">{{ energyValue }}</div>
					</div>
					<div class="energy-label">{{ t("index.energy.todayLabel") }}</div>
				</div>

				<div class="charts-section" :class="{ 'charts-section--lock': !showEnergyCharts }">
					<div v-if="showEnergyCharts" class="energy-charts">
						<div v-for="item in scoreList" :key="item.title" class="chart-item">
							<div class="chart-bar-wrap">
								<div
									class="chart-bar"
									:style="{ height: `${item.score}%` }"
									:class="{ animate: animateReady }"
								></div>
							</div>
							<span class="chart-score">{{ item.score }}</span>
							<span class="chart-title">{{ item.title }}</span>
						</div>
					</div>
					<template v-else>
						<div class="energy-lock">
							<img src="../../../images/pc/mohu.png" class="energy-lock-img" alt="" />
							<p class="energy-lock-text">{{ energyLockText }}</p>
						</div>
						<div class="energy-categories">
							<span v-for="item in categoryList" :key="item">{{ item }}</span>
						</div>
					</template>
				</div>
			</div>

			<div class="energy-detail-link" @click="handleEnergyDetailClick">
				<span>{{ t("index.energy.viewDetail") }}</span>
				<img src="../../../images/pc/guide.png" class="guide-arrow" alt="" />
			</div>
		</div>
	</section>
</template>

<script setup>
import { computed, onMounted, ref, watch, nextTick, inject } from "vue";
import { storeToRefs } from "pinia";
import { useI18n } from "~/composables/useI18n";
import { useLangImg } from "~/composables/useLangImg";
import { useUserStore } from "~/stores/user";
import { getHomeCalendar, getHomeEnergy } from "~/api/api";
import {
	useHomepageTracking,
	useSectionView,
} from "@variants/index/composables/useHomepageTracking";
const { t, locale } = useI18n();
const { isLogin } = storeToRefs(useUserStore());
const { track } = useHomepageTracking("mobile");
const openLoginPopup = inject("openLoginPopup", null);

const dailyEnergyRef = ref(null);
const almanacRef = ref(null);

const energyData = ref(null);
const calendarData = ref(null);
const dataLoading = ref(true);
const profileMissing = ref(false);
const animateReady = ref(false);
const gaugeRenderKey = ref(0);

const getLangImg = useLangImg(
	import.meta.glob("../../../images/pc/*/*.{png,jpg,jpeg,webp,svg,gif}", {
		eager: true,
		import: "default",
	})
);

const categoryList = computed(() => [
	t("index.energy.categories.emotion"),
	t("index.energy.categories.career"),
	t("index.energy.categories.wealth"),
	t("index.energy.categories.travel"),
	t("index.energy.categories.health"),
]);

const totalScore = computed(() => energyData.value?.yunShiTotalScore?.score ?? 0);

/** 已登录且有档案时才展示分项能量图 */
const showEnergyCharts = computed(() => isLogin.value && !profileMissing.value);

const energyLockText = computed(() =>
	profileMissing.value ? t("index.energy.addProfile") : t("index.energy.mohu")
);

const energyValue = computed(() => {
	if (!isLogin.value || profileMissing.value) return "??";
	return totalScore.value || "--";
});

const scoreList = computed(() => {
	if (!showEnergyCharts.value) return [];
	return energyData.value?.yunShiScore || [];
});

const formatSolarDate = (solar, lang) => {
	const month = String(solar.month).padStart(2, "0");
	const day = String(solar.day).padStart(2, "0");

	if (lang === "en") {
		const date = new Date(solar.year, solar.month - 1, solar.day);
		return new Intl.DateTimeFormat("en-US", {
			year: "numeric",
			month: "long",
			day: "2-digit",
		}).format(date);
	}

	return `${solar.year}年 ${month}月${day}日`;
};

const tsData = computed(() => {
	const riLi = calendarData.value?.riLi;
	if (!riLi) {
		return { date: "", week: "", lunar: "", ganzhiItems: [] };
	}

	const { solar, lunar, baZi, weekL, weekEn } = riLi;
	const isEn = locale.value === "en";

	return {
		date: formatSolarDate(solar, locale.value),
		week: isEn ? weekEn : weekL,
		lunar: `${lunar.month}${lunar.day}`,
		ganzhiItems: isEn
			? [
					{ label: t("index.energy.ts.ganzhi.year"), value: baZi.year },
					{ label: t("index.energy.ts.ganzhi.month"), value: baZi.month },
					{ label: t("index.energy.ts.ganzhi.day"), value: baZi.day },
				]
			: [{ value: `${baZi.year}年` }, { value: `${baZi.month}月` }, { value: `${baZi.day}日` }],
	};
});

const getTodayYmd = (date = new Date()) => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}${month}${day}`;
};

const almanacDate = computed(() => getTodayYmd());

useSectionView(dailyEnergyRef, {
	track,
	sectionId: "daily_energy",
	getExtraParams: () => {
		if (!isLogin.value) return {};
		const score = totalScore.value;
		return score > 0 ? { energy_score: score } : {};
	},
});

useSectionView(almanacRef, {
	track,
	sectionId: "almanac",
	getExtraParams: () => ({ almanac_date: almanacDate.value }),
});

const handleEnergyDetailClick = () => {
	track("energy_detail_click", {
		element_id: "energy_detail",
		...(isLogin.value && totalScore.value > 0 ? { energy_score: totalScore.value } : {}),
	});

	if (!isLogin.value) {
		if (typeof openLoginPopup === "function") {
			openLoginPopup(async () => {
				await getEnergyData();
				// 登录后若无档案：留在本页展示「添加档案」，不跳转
				if (profileMissing.value) return;
				navigateTo("/seer/daily-energy");
			});
			return;
		}
		navigateTo("/seer/login");
		return;
	}

	if (profileMissing.value) {
		navigateTo("/seer/user?type=default&from=daily-energy");
		return;
	}

	navigateTo("/seer/daily-energy");
};

const handleAlmanacDetailClick = () => {
	track("almanac_detail_click", {
		element_id: "almanac_detail",
		almanac_date: almanacDate.value,
	});
	navigateTo("/seer/huangli");
};

const getTsData = async () => {
	try {
		const res = await getHomeCalendar({ date: getTodayYmd() });
		calendarData.value = res?.data || null;
	} catch (error) {
		console.error("獲取通勝數據失敗:", error);
	}
};

const getEnergyData = async () => {
	if (!isLogin.value) {
		energyData.value = null;
		profileMissing.value = false;
		return;
	}

	try {
		const res = await getHomeEnergy();
		energyData.value = res?.data || null;
		profileMissing.value = false;
	} catch (error) {
		console.error("獲取今日能量數據失敗:", error);
		energyData.value = null;
		profileMissing.value = error?.code === 1303;
	}
};

onMounted(async () => {
	try {
		await Promise.all([getTsData(), getEnergyData()]);
		await nextTick();
		animateReady.value = true;
	} finally {
		dataLoading.value = false;
	}
});

watch([locale, isLogin], async () => {
	dataLoading.value = true;
	animateReady.value = false;
	gaugeRenderKey.value += 1;
	try {
		await Promise.all([getTsData(), getEnergyData()]);
		await nextTick();
		animateReady.value = true;
	} finally {
		dataLoading.value = false;
	}
});

const SEGMENT_COUNT = 22;

const gaugeActiveCount = computed(() => {
	if (!isLogin.value) return 15;
	if (profileMissing.value) return 0;
	return Math.round(SEGMENT_COUNT * (totalScore.value / 100));
});

const gaugeSegments = computed(() => {
	const cx = 110;
	const cy = 110;
	const radius = 85;
	const segW = 9;
	const segH = 22;
	const startAngle = Math.PI;
	const endAngle = Math.PI * 2;
	const activeCount = gaugeActiveCount.value;

	return Array.from({ length: SEGMENT_COUNT }, (_, index) => {
		const ratio = index / (SEGMENT_COUNT - 1);
		const angle = startAngle + ratio * (endAngle - startAngle);
		const pivotX = cx + radius * Math.cos(angle);
		const pivotY = cy + radius * Math.sin(angle);

		return {
			x: pivotX - segW / 2,
			y: pivotY - segH / 2,
			cx: pivotX,
			cy: pivotY,
			rotate: (angle * 180) / Math.PI + 90,
			active: index < activeCount,
		};
	});
});
</script>

<style lang="scss" scoped>
@keyframes numScaleUp {
	0% {
		opacity: 0;
		transform: translateX(-50%) scale(0.6);
	}
	100% {
		opacity: 1;
		transform: translateX(-50%) scale(1);
	}
}
@keyframes fadeIn {
	0% {
		opacity: 0;
	}
	100% {
		opacity: 1;
	}
}
@keyframes barRise {
	0% {
		transform: translateY(100%);
	}
	100% {
		transform: translateY(0);
	}
}
@keyframes segLightUp {
	0% {
		fill: #f5ebd4;
	}
	100% {
		fill: #f0a820;
	}
}

.energy-skeleton {
	display: flex;
	flex-direction: column;
	gap: 24px;
}
.energy-skeleton__card {
	min-height: 300px;
	padding: 28px;
	border-radius: 20px;
	background: #fff;
	box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
	box-sizing: border-box;
}
.energy-skeleton__title,
.energy-skeleton__body span,
.energy-skeleton__body i {
	display: block;
	background: linear-gradient(90deg, #eee 25%, #fafafa 42%, #eee 62%);
	background-size: 300% 100%;
	animation: home-energy-m-shimmer 1.35s ease-in-out infinite;
}
.energy-skeleton__title {
	width: 220px;
	height: 34px;
	margin: 0 auto 34px;
	border-radius: 7px;
}
.energy-skeleton__body {
	display: grid;
	grid-template-columns: 150px 1fr;
	gap: 28px;
	align-items: center;
}
.energy-skeleton__body span {
	grid-row: 1/4;
	width: 150px;
	height: 150px;
	border-radius: 50%;
}
.energy-skeleton__body i {
	height: 22px;
	border-radius: 6px;
}
.energy-skeleton__body i:last-child {
	width: 68%;
}
@keyframes home-energy-m-shimmer {
	to {
		background-position: -100% 0;
	}
}

.m-energy {
	padding: 24px 24px 0;

	.ts-card,
	.energy-card {
		background: #ffffff;
		border-radius: 20px;
		box-shadow: 0 4px 24.6px rgba(0, 0, 0, 0.1);
	}

	.ts-card {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 16px;
		padding: 28px 24px;
		margin-bottom: 30px;

		.ts-date {
			flex: 1;
			min-width: 0;
			opacity: 0;

			&.animate {
				animation: fadeIn 0.7s ease-out 0.2s forwards;
			}

			.solar {
				margin: 0;
				color: #333333;
				font-size: 26px;
				line-height: 34px;
			}

			.lunar {
				margin: 8px 0 0;
				font-size: 40px;
				line-height: 1.2;
				color: #333333;

				&--en {
					font-size: 32px;
				}
			}

			.ganzhi {
				margin-top: 8px;

				&--en {
					display: flex;
					gap: 20px;

					.ganzhi-item {
						display: flex;
						align-items: center;
						flex-direction: column;
						gap: 4px;
					}

					.ganzhi-value {
						font-size: 22px;
						line-height: 28px;
						color: #333333;
					}

					.ganzhi-label {
						font-size: 18px;
						line-height: 1;
						color: #888888;
					}
				}
			}

			.ganzhi-text {
				margin: 0;
				display: flex;
				flex-wrap: wrap;
				gap: 8px;
				font-size: 26px;
				line-height: 34px;
				color: #333333;
			}
		}

		.ts-link {
			flex-shrink: 0;
			display: inline-flex;
			align-items: center;
			gap: 4px;
			font-size: 24px;
			line-height: 34px;
			color: #333333;
			cursor: pointer;
			white-space: nowrap;

			.arrow {
				width: 24px;
				height: 24px;
			}
		}
	}

	.energy-card {
		padding: 32px 24px 28px;

		.energy-main {
			display: flex;
			flex-direction: row;
			flex-wrap: nowrap;
			align-items: flex-end;
			gap: 16px;

			.gauge-section {
				flex-shrink: 0;
				display: flex;
				flex-direction: column;
				align-items: center;
				width: 220px;

				.gauge-wrap {
					position: relative;
					width: 200px;
					height: 110px;

					.gauge {
						display: block;
						width: 100%;
						height: auto;

						rect.animate {
							animation: segLightUp 0.35s ease-out forwards;
						}
					}

					.gauge-value {
						position: absolute;
						left: 50%;
						bottom: 0;
						transform: translateX(-50%);
						font-family:
							"Source Han Sans CN", "PingFang SC", "Microsoft YaHei", sans-serif !important;
						font-size: 56px;
						line-height: 1;
						color: #333333;
						letter-spacing: 2px;
						opacity: 0;

						&.animate {
							animation: numScaleUp 0.8s ease-out forwards;
						}
					}
				}

				.energy-label {
					display: inline-flex;
					align-items: center;
					justify-content: center;
					min-width: 120px;
					padding: 6px 20px;
					border: 1px solid #f2e5b5;
					border-radius: 999px;
					background: #ffffff;
					font-size: 24px;
					color: #333333;
					margin-top: 8px;
				}
			}

			.charts-section {
				flex: 1;
				min-width: 0;
				min-height: 180px;
				padding-bottom: 4px;

				.energy-charts {
					display: flex;
					flex-direction: row;
					flex-wrap: nowrap;
					justify-content: space-between;
					align-items: flex-end;
					width: 100%;
					height: 100%;
				}

				.chart-item {
					display: flex;
					flex-direction: column;
					align-items: center;
					flex: 1 1 0;
					min-width: 0;
				}

				.chart-bar-wrap {
					width: 14px;
					height: 100px;
					display: flex;
					align-items: flex-end;
					background: #f3ecd4;
					border-radius: 999px;
					overflow: hidden;
					margin: 6px 0;
				}

				.chart-bar {
					width: 100%;
					border-radius: 999px;
					background: linear-gradient(to bottom, #f2af19 0%, #ffe790 100%);
					min-height: 20px;
					transform: translateY(100%);

					&.animate {
						animation: barRise 0.9s ease-out 0.2s forwards;
					}
				}

				.chart-score {
					font-size: 28px;
					line-height: 1;
					color: #333333;
					margin: 10px 0;
				}

				.chart-title {
					font-size: 24px;
					line-height: 26px;
					color: #333333;
					white-space: nowrap;
				}

				.energy-lock {
					position: relative;
					width: 100%;
					height: 100px;

					.energy-lock-img {
						width: 100%;
						height: 100%;
						object-fit: contain;
					}

					.energy-lock-text {
						width: 100%;
						position: absolute;
						top: 50%;
						left: 50%;
						transform: translate(-50%, -50%);
						font-size: 24px;
						line-height: 26px;
						color: #333333;
						text-align: center;
					}
				}

				.energy-profile-tip {
					display: flex;
					align-items: center;
					justify-content: center;
					width: 100%;
					min-height: 100px;

					p {
						margin: 0;
						font-size: 24px;
						line-height: 30px;
						color: #333333;
						text-align: center;
					}
				}

				.energy-categories {
					display: none;
				}

				&--lock {
					display: flex;
					flex-direction: column;
					align-items: stretch;
					justify-content: center;
					min-height: 140px;

					.energy-lock {
						width: 100%;
						height: 80px;
					}

					.energy-categories {
						display: flex;
						justify-content: space-between;
						margin-top: 12px;
						font-size: 22px;
						line-height: 26px;
						color: #333333;
					}
				}
			}
		}

		.energy-detail-link {
			display: inline-flex;
			align-items: center;
			justify-content: center;
			gap: 6px;
			width: 100%;
			margin-top: 28px;
			font-size: 26px;
			line-height: 34px;
			color: #333333;
			cursor: pointer;

			.guide-arrow {
				width: 24px;
				height: 24px;
			}
		}
	}

	&--en .energy-card .energy-main .charts-section .chart-title {
		font-size: 19px;
		line-height: 18px;
		white-space: normal;
	}

	&--en .ts-card .ts-date .solar {
		font-size: 20px;
		line-height: 28px;
	}

	&--en .ts-card .ts-date .lunar {
		font-size: 32px;
	}

	&--en .energy-card .charts-section--lock .energy-categories {
		font-size: 20px;
		line-height: 20px;
	}
}
</style>
